import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { Chip, MenuItem, Select, styled } from 'opensolar-ui'
import React, { useMemo, useState } from 'react'
import { FormDataConsumer, useTranslate } from 'react-admin'
import { useField, useForm } from 'react-final-form'
import { useSelector } from 'react-redux'
import { useResourceStyles } from 'resources/styles'
import { ConnectedOrgBrief } from 'types/orgs'
import { urlToId } from 'util/misc'

const SelectComponentWrapper = styled('div', { name: 'SelectComponentWrapper' })(({ theme }) => ({
  backgroundColor: theme.palette.coolGrey[100],
  padding: 16,
  borderRadius: 4,
}))
interface PropTypes {
  dependsOn?: string | Function // If you want this to conditionally render based on another field in the form, pass it in here
  dependsOnValue?: any // If the field should equal a particular value, add here, otherwise any truthy value will work
  noBorder?: true
  disabled?: boolean
  record?: { org?: string }
  extraAction?: React.ReactNode
}

const ShareabilitySelector: React.FC<PropTypes> = ({
  dependsOn,
  dependsOnValue,
  noBorder,
  disabled,
  record,
  ...props
}) => {
  const source = 'share_with_orgs'
  const translate = useTranslate()
  const resourceStyles = useResourceStyles()
  const form = useForm()
  const {
    input: { value: selected = [], onChange: changeSelected },
  } = useField(source, { subscription: { value: true } })
  const connectedOrgs = useSelector(orgSelectors.getConnectedOrgs)
  const connectedOrgsFiltered = connectedOrgs?.filter((x) => x.is_active === true)
  const [showSelector, setShowSelector] = useState(!dependsOn)
  const [choices, setChoices] = useState<Partial<ConnectedOrgBrief>[]>(connectedOrgsFiltered)

  const userOrgId = useSelector(authSelectors.getOrgId)
  const entityOrgId = urlToId(record?.org)
  const isShared = entityOrgId !== undefined && userOrgId !== entityOrgId

  const selectAllOption: Partial<ConnectedOrgBrief> = {
    org_name: translate('Select all'),
    org_url: '*',
  }

  const matchSuggestion = (filterValue: string, choice: ConnectedOrgBrief) =>
    choice.org_name.toLocaleLowerCase().match(filterValue.toLocaleLowerCase())

  const setFilter = useMemo(
    () => (filterValue: string) => {
      const terms = filterValue.toLocaleLowerCase().split(' ')
      const filtered: { org: ConnectedOrgBrief; score: number }[] = []
      for (const orgCon of connectedOrgsFiltered) {
        let score = 0
        const orgName = orgCon.org_name.toLocaleLowerCase()
        for (const term of terms) {
          if (orgCon.org_id.toString() === term || orgName === term) {
            // Match exact ID or name
            score += 100
          } else if (orgName.split(' ').indexOf(term) !== -1) {
            // Match a word in name
            score += 10
          } else if (orgCon.org_id.toString().startsWith(term) || orgName.match(term)) {
            // Match start of ID or some part of name
            score += 1
          }
        }
        if (score > 0) filtered.push({ org: orgCon, score })
      }
      filtered.sort((o1, o2) => o2.score - o1.score)
      let list: Partial<ConnectedOrgBrief>[] = filtered.length ? filtered.map((o) => o.org) : connectedOrgsFiltered
      if (!selected || selected?.length < connectedOrgsFiltered.length) list = list.concat([selectAllOption])
      setChoices(list) // Show all if no matches
    },
    [connectedOrgsFiltered]
  )

  const clearAll = () => {
    changeSelected([])
  }

  const selectAll = () => {
    const urls: string[] = []
    for (const orgConn of connectedOrgsFiltered) urls.push(orgConn.org_url)
    form.change(source, urls)
  }

  const onSelectChange = (values: string[]) => {
    if (!values) return
    // Handle special commands
    if (values.includes('__NONE__')) {
      changeSelected([])
      return
    }
    if (values.includes('*')) {
      selectAll()
      return
    }
    changeSelected(values)
  }

  const wrapperClass = noBorder ? undefined : resourceStyles.formSection

  // Hide this control when viewing an entity from another org
  if (isShared) return null

  return (
    <>
      {dependsOn && (
        <FormDataConsumer>
          {({ formData }) => {
            const value = typeof dependsOn === 'string' ? formData[dependsOn] : dependsOn(formData)
            if (dependsOnValue === undefined) setShowSelector(value !== undefined && value !== null)
            else setShowSelector(value === dependsOnValue)
            return <></>
          }}
        </FormDataConsumer>
      )}
      {showSelector && (connectedOrgsFiltered.length || 0) > 0 && (
        <SelectComponentWrapper>
          <Select
            multiple
            fullWidth
            label={translate('Share with the following organisations:')}
            disabled={disabled}
            value={selected || []}
            onOpen={() => setFilter('')}
            onChange={(e: any) => onSelectChange((e.target.value || []) as string[])}
            renderValue={(urls: any) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(urls as string[]).map((u) => {
                  const name = choices.find((c) => c.org_url === u)?.org_name || u
                  return <Chip key={u} size="small" variant="outlined" label={name} />
                })}
              </div>
            )}
            MenuProps={{
              PaperProps: { style: { maxHeight: 360 } },
            }}
          >
            {/* Unselect all helper */}
            <MenuItem key="__NONE__" value="__NONE__" disabled={!selected || selected.length === 0}>
              {translate('Unselect all')}
            </MenuItem>
            {choices.map((opt) => (
              <MenuItem key={opt.org_url || String(opt.org_id)} value={opt.org_url as string}>
                {opt.org_name}
              </MenuItem>
            ))}
          </Select>
          {props.extraAction}
        </SelectComponentWrapper>
      )}
    </>
  )
}

export default ShareabilitySelector
